import { FC, useEffect, useState } from 'react';

import { Box } from '@mui/material';

const isImageFile = (name: string | null | undefined): boolean => {
  if (!name) return false;
  return /\.(jpe?g|png|gif|webp|svg|bmp)$/i.test(name);
};

interface CommentAttachmentsProps {
  files: { objectId?: string | null; name?: string | null }[];
  resourceId: string;
  downloadCommentFile: (resourceId: string, documentId: string) => Promise<Blob>;
}

const CommentAttachmentThumb: FC<{
  file: { objectId?: string | null; name?: string | null };
  resourceId: string;
  downloadCommentFile: (resourceId: string, documentId: string) => Promise<Blob>;
}> = ({ file, resourceId, downloadCommentFile }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!file.objectId) return;
    let objectUrl: string | null = null;
    downloadCommentFile(resourceId, file.objectId)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.objectId, resourceId]);

  if (!url) return null;

  if (isImageFile(file.name)) {
    return (
      <>
        <Box
          component="img"
          src={url}
          alt={file.name ?? ''}
          onClick={() => setOpen(true)}
          sx={{
            width: 64,
            height: 64,
            objectFit: 'cover',
            borderRadius: '6px',
            cursor: 'pointer',
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        />
        {open && (
          <Box
            onClick={() => setOpen(false)}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1300,
              background: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={url}
              alt={file.name ?? ''}
              sx={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </Box>
        )}
      </>
    );
  }

  return (
    <Box
      component="a"
      href={url}
      download={file.name ?? 'file'}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.5,
        borderRadius: '6px',
        border: '1px solid',
        borderColor: 'grey.200',
        cursor: 'pointer',
        textDecoration: 'none',
        color: 'text.primary',
        fontSize: '0.75rem',
        maxWidth: 200,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        '&:hover': { borderColor: 'grey.400', textDecoration: 'underline' },
      }}
    >
      {file.name}
    </Box>
  );
};

const CommentAttachments: FC<CommentAttachmentsProps> = ({
  files,
  resourceId,
  downloadCommentFile,
}) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
    {files
      .filter((f) => f.objectId)
      .map((file) => (
        <CommentAttachmentThumb
          key={file.objectId}
          file={file}
          resourceId={resourceId}
          downloadCommentFile={downloadCommentFile}
        />
      ))}
  </Box>
);

export default CommentAttachments;
